import React, { useEffect, useState } from "react";
import { api, downloadAuthFile, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Cell,
} from "recharts";

const RANGE = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const DEVICE_COLORS = { desktop: "#0055FF", mobile: "#111111", tablet: "#F97316" };

const Stat = ({ label, value, sub }) => (
  <div className="border border-black/10 p-5 bg-white">
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="mt-2 font-display font-black text-3xl tracking-tight tabular-nums">{value}</div>
    {sub && <div className="mt-1 text-[11px] font-mono text-slate-500">{sub}</div>}
  </div>
);

const RankTable = ({ title, rows, cols, testId }) => (
  <div className="border border-black/10 bg-white p-6" data-testid={testId}>
    <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">{title}</div>
    {rows.length === 0 ? (
      <div className="text-sm font-mono text-slate-400">No data yet.</div>
    ) : (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <span className="w-6 text-[10px] font-mono text-slate-400">{i + 1}.</span>
            <span className="flex-1 truncate font-mono text-xs">{r[cols[0]]}</span>
            <span className="font-display font-bold tabular-nums">{r[cols[1]]}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function AdminTraffic() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/traffic?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  if (!data) return <div className="text-sm font-mono text-slate-500">Loading…</div>;

  const t = data.totals;

  return (
    <div data-testid="admin-traffic">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">Analytics</div>
      <div className="mt-2 flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Traffic</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1">
            {RANGE.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`traffic-range-${r.days}`}
                className={`px-4 h-9 text-[11px] font-mono uppercase tracking-[0.15em] rounded-full transition-colors ${
                  days === r.days ? "bg-ink text-white" : "bg-secondary hover:bg-ink/10"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="traffic-export-btn"
            onClick={async () => {
              try {
                await downloadAuthFile("/admin/traffic/export.xlsx?days=90", "shamstrade-traffic-90d.xlsx");
              } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || "Download failed");
              }
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-ink text-white hover:bg-electric text-[11px] font-mono uppercase tracking-[0.15em]"
          >
            ⬇ 90-day report (Excel)
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Page views" value={t.page_views} sub={`Last ${data.days} days`} />
        <Stat label="Unique visitors" value={t.unique_visitors} sub="By session" />
        <Stat label="Product clicks" value={t.product_clicks} sub="Cards opened" />
        <Stat label="Add to cart" value={t.add_to_carts} sub="Items added" />
      </div>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Checkout starts" value={t.checkout_starts} sub="Reached checkout" />
        <Stat label="WhatsApp clicks" value={t.whatsapp_clicks} sub="Chat button" />
        <Stat label="Call clicks" value={t.call_clicks} sub="Phone button" />
        <Stat label="Searches" value={t.searches} sub="Shop search" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 border border-black/10 bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">
            Views &amp; visitors per day
          </div>
          <div style={{ width: "100%", height: 300, minHeight: 300 }}>
            <ResponsiveContainer>
              <LineChart data={data.views_by_day}>
                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="day" stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <YAxis stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#111111", border: "none", color: "#fff", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="views" stroke="#0055FF" strokeWidth={2.5} dot={{ fill: "#111111", r: 3 }} />
                <Line type="monotone" dataKey="visitors" stroke="#F97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex gap-5 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-[#0055FF]">— Views</span>
            <span className="text-[#F97316]">— Visitors</span>
          </div>
        </div>

        <div className="border border-black/10 bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">Devices</div>
          <div style={{ width: "100%", height: 300, minHeight: 300 }}>
            <ResponsiveContainer>
              <BarChart data={data.devices}>
                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="device" stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <YAxis stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#111111", border: "none", color: "#fff", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Bar dataKey="count">
                  {data.devices.map((d, i) => (
                    <Cell key={i} fill={DEVICE_COLORS[d.device] || "#9CA3AF"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-5">
        <RankTable title="Top pages" rows={data.top_pages} cols={["path", "views"]} testId="traffic-top-pages" />
        <RankTable
          title="Top products (clicks)"
          rows={data.top_products.map((p) => ({ label: p.name || p.slug, clicks: p.clicks }))}
          cols={["label", "clicks"]}
          testId="traffic-top-products"
        />
        <RankTable title="Top searches" rows={data.top_searches} cols={["term", "count"]} testId="traffic-top-searches" />
      </div>
    </div>
  );
}
